import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { cn } from '@/lib/utils';

// Espacio duro (U+00A0). Se usa para materializar líneas en blanco extra como
// párrafos visibles (una línea con solo un espacio normal markdown la ignora).
const NBSP = String.fromCharCode(0xa0);

/**
 * Normaliza el texto antes de renderizarlo:
 *  - Inserta una línea en blanco antes de listas / citas / títulos para que se
 *    reconozcan aunque el usuario los escriba justo debajo de un párrafo.
 *  - Respeta las líneas en blanco EXTRA que deja el usuario (cada salto de más
 *    se convierte en un párrafo vacío visible) → se conservan los espacios.
 */
export function normalizeMarkdown(src?: string | null): string {
  if (!src) return '';
  let text = src.replace(/\r\n/g, '\n');

  const isBlock = (l: string) => /^\s*([-*+]\s+|\d+\.\s+|>\s?|#{1,6}\s+)/.test(l);
  const lines = text.split('\n');
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const prev = out.length ? out[out.length - 1] : null;
    if (isBlock(line) && prev !== null && prev.trim() !== '' && !isBlock(prev)) {
      out.push(''); // separa el bloque del párrafo anterior
    }
    out.push(line);
  }
  text = out.join('\n');

  // Cada línea en blanco más allá de la primera (separador normal de párrafo)
  // se materializa como un párrafo con espacio duro → mantiene la separación.
  text = text.replace(/\n{2,}/g, (m) => '\n\n' + (NBSP + '\n\n').repeat(m.length - 2));

  return text;
}

/**
 * Renderiza texto/markdown respetando los saltos de párrafo y de línea, con
 * viñetas en forma de flecha. Si el usuario usa markdown (**negrita**, listas,
 * títulos…) también se renderiza.
 */
export function RichText({ children, className }: { children?: string | null; className?: string }) {
  return (
    <div
      className={cn(
        'prose prose-sm max-w-none dark:prose-invert',
        // Listas con viñeta tipo flecha
        '[&_ul]:list-none [&_ul]:pl-1 [&_ul>li]:relative [&_ul>li]:pl-5',
        "[&_ul>li]:before:absolute [&_ul>li]:before:left-0 [&_ul>li]:before:font-bold [&_ul>li]:before:text-primary [&_ul>li]:before:content-['▸']",
        // Listas numeradas visibles
        '[&_ol]:list-decimal [&_ol]:pl-5',
        '[&_li]:my-0.5',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkBreaks]}>{normalizeMarkdown(children)}</ReactMarkdown>
    </div>
  );
}

export default RichText;
